import { onCLS, onINP, onLCP, onFCP, onTTFB, Metric } from 'web-vitals';

/**
 * Core Web Vitals Tracking Utility
 * 
 * Tracks and reports key performance metrics:
 * - LCP (Largest Contentful Paint): Visual loading performance
 * - INP (Interaction to Next Paint): Interactivity and responsiveness (replaces FID)
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Initial render time
 * - TTFB (Time to First Byte): Server response time
 */

type AnalyticsHandler = (metric: Metric) => void;

/**
 * Send metric to analytics service (customize based on your analytics provider)
 */
const sendToAnalytics = (metric: Metric): void => {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
    });
  }

  // Send to analytics in production
  if (process.env.NODE_ENV === 'production') {
    // Example: Google Analytics 4
    if (window.gtag) {
      window.gtag('event', metric.name, {
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        event_category: 'Web Vitals',
        event_label: metric.id,
        non_interaction: true,
      });
    }

    // Example: Custom analytics endpoint
    fetch('/api/analytics/web-vitals', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id,
        navigationType: metric.navigationType,
      }),
      keepalive: true,
    }).catch((error) => {
      // Fail silently to avoid impacting user experience
      console.error('[Web Vitals] Failed to send metric:', error);
    });
  }
};

/**
 * Get performance rating color for UI display
 */
export const getRatingColor = (rating: 'good' | 'needs-improvement' | 'poor'): string => {
  switch (rating) {
    case 'good':
      return 'text-green-600';
    case 'needs-improvement':
      return 'text-yellow-600';
    case 'poor':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Format metric value for display
 */
export const formatMetricValue = (name: string, value: number): string => {
  if (name === 'CLS') {
    return value.toFixed(3);
  }
  return `${Math.round(value)}ms`;
};

/**
 * Initialize Core Web Vitals tracking
 * Call this once when your app starts (typically in index.tsx or App.tsx)
 */
export const reportWebVitals = (onPerfEntry?: AnalyticsHandler): void => {
  const handler = onPerfEntry || sendToAnalytics;

  // Core Web Vitals
  onCLS(handler);  // Cumulative Layout Shift
  onINP(handler);  // Interaction to Next Paint (replaces FID)
  onLCP(handler);  // Largest Contentful Paint

  // Additional metrics
  onFCP(handler);  // First Contentful Paint
  onTTFB(handler); // Time to First Byte
};

/**
 * Performance thresholds based on Web Vitals recommendations
 */
export const PERFORMANCE_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },      // Largest Contentful Paint (ms)
  INP: { good: 200, poor: 500 },        // Interaction to Next Paint (ms)
  CLS: { good: 0.1, poor: 0.25 },       // Cumulative Layout Shift (score)
  FCP: { good: 1800, poor: 3000 },      // First Contentful Paint (ms)
  TTFB: { good: 800, poor: 1800 },      // Time to First Byte (ms)
};

/**
 * Check if metric value is within "good" threshold
 */
export const isGoodMetric = (name: string, value: number): boolean => {
  const threshold = PERFORMANCE_THRESHOLDS[name as keyof typeof PERFORMANCE_THRESHOLDS];
  return threshold ? value <= threshold.good : false;
};

/**
 * TypeScript declaration for gtag (Google Analytics)
 */
declare global {
  interface Window {
    gtag?: (
      command: string,
      eventName: string,
      eventParams: Record<string, unknown>
    ) => void;
  }
}

export default reportWebVitals;
